/**
 * Sunset redirect for the old standalone Meta campaigns workspace (#4173 Chunk 6).
 *
 * The Meta-specific accordion page that used to live here was replaced by the unified ``/campaigns``
 * surface across Chunks 1-3: campaigns sidebar shows both Google + Meta, ``/campaigns/meta/{id}`` is
 * the per-Meta-campaign detail route, and ``/new/campaign/meta`` is the Meta wizard.
 *
 * This file stays so that any external links / bookmarks landing on ``/meta-campaigns`` don't 404 —
 * the route now redirects in ``beforeLoad`` to the most useful unified URL we can determine:
 *
 *   - If the user has ≥1 Meta campaign  → ``/campaigns/meta/{first_meta_id}`` (lands on Meta detail).
 *   - Otherwise                          → ``/campaigns`` (the existing bare-route redirect logic
 *                                          decides whether to land on a Google campaign or the empty
 *                                          state).
 *
 * The original page rendered ``CreateMetaCampaignsCard`` + a per-offer accordion of campaigns; both
 * have replacements:
 *   - ``CreateMetaCampaignsCard``     → ``routes/_authenticated/new/_layout/campaign/meta.tsx``
 *   - The campaign accordion bodies   → ``components/campaign/meta/meta-campaign-detail-panel.tsx``
 */

import { createFileRoute, redirect } from '@tanstack/react-router';

import { metaCampaignsQueryOptions } from '@/lib/query-options';

export const Route = createFileRoute('/_authenticated/_layout/meta-campaigns/')({
  beforeLoad: async ({ context }) => {
    let target: { to: string; params?: Record<string, string> } = { to: '/campaigns' };
    try {
      const campaigns = await context.queryClient.fetchQuery(metaCampaignsQueryOptions());
      if (campaigns.length > 0) {
        target = {
          to: '/campaigns/meta/$metaCampaignSlug',
          params: { metaCampaignSlug: campaigns[0].slug },
        };
      }
    } catch {
      // Network / auth hiccup → fall back to the bare /campaigns redirect rather than throwing 500.
    }
    throw redirect(target);
  },
});
